import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  useTheme,
  useMediaQuery
} from '@mui/material';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

// Styled components
const TopbarArea = styled(Box)(({ theme, trigger }) => ({
  backgroundColor: 'transparent',
  borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
  padding: '10px 0',
  color: '#ffffff',
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  zIndex: 1001,
  opacity: trigger ? 0 : 1,
  visibility: trigger ? 'hidden' : 'visible',
  transform: trigger ? 'translateY(-100%)' : 'translateY(0)',
  transition: 'all 0.3s ease',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TopbarContainer = styled(Container)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const TopbarContact = styled(Box)({
  display: 'flex',
  gap: '20px',
});

const ContactItem = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '5px',
});

const TopbarSocial = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
});

const MainMenuBar = styled(Box)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  zIndex: 999,
  padding: '0',
  display: 'flex',
  justifyContent: 'center',
  top: '80px',
  [theme.breakpoints.down('md')]: {
    top: '0'
  }
}));

const StyledAppBar = styled(AppBar)(({ theme, trigger }) => ({
  backgroundColor: '#fff',
  boxShadow: '0 0 20px rgba(0, 0, 0, .1)',
  transition: 'all 0.3s ease',
  position: 'fixed',
  top: 0,
  width: '100%',
  zIndex: 999,
  visibility: trigger ? 'visible' : 'hidden',
  opacity: trigger ? 1 : 0,
  transform: trigger ? 'translateY(0)' : 'translateY(-100%)',
  animation: trigger ? `${stickyAnimation} 0.95s forwards` : 'none'
}));

const stickyAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-100%);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LogoContainer = styled(Box)({
  flexGrow: 0,
  display: 'flex',
  alignItems: 'center',
});

const NavContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginLeft: 'auto',
  marginRight: 'auto',
});

const NavButton = styled(Button)(({ theme, active, trigger }) => ({
  color: active ? theme.palette.primary.main : trigger ? '#333' : '#fff',
  fontWeight: 600,
  margin: '0 15px',
  fontSize: '16px',
  padding: '8px 0',
  textTransform: 'capitalize',
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
  },
}));

const DrawerContainer = styled(Box)({
  width: 500,
  padding: '30px',
  backgroundColor: '#101010',
  color: '#ffffff',
  height: '100%',
});

const CanvasLogo = styled(Box)({
  marginBottom: '20px',
  textAlign: 'center',
});

const CanvasText = styled(Typography)({
  marginBottom: '30px',
  color: '#ffffff',
  textAlign: 'center',
});

const CanvasContact = styled(Box)({
  marginTop: '40px',
});

const ContactList = styled(Box)({
  marginBottom: '25px',
  display: 'flex',
  gap: '15px',
  alignItems: 'flex-start',
});

const HamburgerMenu = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  width: '30px',
  height: '30px',
});

const DotRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  marginBottom: '3px',
});

const Dot = styled(Box)(({ theme }) => ({
  width: '5px',
  height: '5px',
  borderRadius: '50%',
  backgroundColor: '#bb0b0b',
  margin: '0 2px',
}));

const CloseButton = styled(Box)({
  position: 'absolute',
  top: '20px',
  right: '20px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

const CloseLine = styled(Box)({
  width: '30px',
  height: '2px',
  backgroundColor: '#ffffff',
  margin: '2px 0',
  '&:first-of-type': {
    transform: 'rotate(45deg) translate(5px, 5px)',
  },
  '&:last-of-type': {
    transform: 'rotate(-45deg)',
  },
});

const CanvasMenuItem = styled(ListItem)({
  padding: '10px 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiListItemText-primary': {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 500,
  },
});

const ContactTitle = styled(Typography)({
  fontSize: '22px',
  fontWeight: 600,
  marginBottom: '20px',
  color: '#ffffff',
});

const ContactInfo = styled(Typography)({
  color: '#ffffff',
  fontSize: '15px',
});

const MediaImage = styled(Box)({
  marginBottom: '30px',
  '& img': {
    width: '100%',
    borderRadius: '5px',
  },
});

function HeaderMain() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 60,
  });
  
  // Add padding to body to account for fixed header and create sticky header styles
  React.useEffect(() => {
    // Add padding to the body element to account for the fixed header
    document.body.style.paddingTop = '0px';
    
    // Create a style element for the sticky header
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      #rs-header.sticky .menu-area {
        background: #fff;
        position: fixed;
        z-index: 999;
        width: 100%;
        top: 0;
        left: 0;
        box-shadow: 0 0 20px rgba(0, 0, 0, .1);
        animation: sticky-animation 0.95s forwards;
      }
      
      #rs-header.sticky .menu-area .MuiAppBar-root {
        background-color: #fff;
      }
      
      @keyframes sticky-animation {
        0% {
          opacity: 0;
          transform: translateY(-100%);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `;
    document.head.appendChild(styleElement);
    
    // Add scroll event listener to handle sticky header animation
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const header = document.getElementById('rs-header');
      
      if (scrollPosition > 10) {
        header?.classList.add('sticky');
      } else {
        header?.classList.remove('sticky');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      document.body.style.paddingTop = '0';
      window.removeEventListener('scroll', handleScroll);
      document.head.removeChild(styleElement);
    };
  }, [isMobile]);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItems = [
    { label: 'Home', href: '#rs-header', active: true },
    { label: 'About', href: '#rs-about', active: false },
    { label: 'Services', href: '#rs-services', active: false },
    { label: 'Portfolios', href: '#rs-project', active: false },
    { label: 'Blog', href: '#rs-blog', active: false },
    { label: 'Contact', href: '#rs-contact', active: false },
  ];

  return (
    <Box sx={{ flexGrow: 1 }} id="rs-header">
      {/* Topbar Area */}
      <TopbarArea trigger={trigger}>
        <TopbarContainer maxWidth="lg">
          <TopbarContact>
            <ContactItem>
              <EmailIcon fontSize="small" />
              <Typography variant="body2" component="a" href="mailto:info@hkmsp.com" sx={{ color: '#fff', textDecoration: 'none', fontWeight: 'bold' }}>
                info@hkmsp.com
              </Typography>
            </ContactItem>
            <ContactItem>
              <PhoneIcon fontSize="small" />
              <Typography variant="body2" component="a" href="tel:(+1)213-349-8776" sx={{ color: '#fff', textDecoration: 'none', fontWeight: 'bold' }}>
                (+1) 213-349-8776
              </Typography>
            </ContactItem>
            <ContactItem>
              <LocationOnIcon fontSize="small" />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                Unit 2A, 17/F, Glenealy Tower, No.1 Glenealy, Central, Hong Kong S.A.R
              </Typography>
            </ContactItem>
          </TopbarContact>
          <TopbarSocial>
            <ContactItem>
              <AccessTimeIcon fontSize="small" />
              <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold' }}>
                Mon - Fri / 9AM - 7PM HKT
              </Typography>
            </ContactItem>
            <IconButton color="inherit" size="small" href="https://www.linkedin.com/company/hk-managed-service-provider" target="_blank">
              <LinkedInIcon fontSize="small" />
            </IconButton>
          </TopbarSocial>
        </TopbarContainer>
      </TopbarArea>

      {/* Main Menu Bar - Always visible until scroll */}
      <MainMenuBar className="main-menu-bar" sx={{ display: trigger ? 'none' : 'flex' }}>
        <Container maxWidth="xl" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', px: { xs: 2, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            position: { xs: 'relative', md: 'absolute' }, 
            left: { xs: 'auto', md: 0 },
            mx: { xs: 'auto', md: 0 }
          }}>
            <Box component="a" href="#rs-header" sx={{ mr: 2 }}>
              <Box 
                component="img" 
                src="/assets/images/logo.png" 
                alt="HK Managed Security Provider" 
                sx={{ 
                  height: { xs: '80px', md: '70px' }, 
                  py: { xs: 2, md: 1 }
                }}
              />
            </Box>
          </Box>
          
          {!isMobile && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexGrow: 1, mr: 0 }}>
              <Box component="nav" sx={{ display: 'flex' }}>
                <Box component="ul" sx={{ 
                  display: 'flex', 
                  listStyle: 'none', 
                  m: 0, 
                  p: 0,
                  height: '100%',
                  alignItems: 'center'
                }}>
                  {menuItems.map((item) => (
                    <Box component="li" key={item.label} sx={{ mx: 0 }}>
                      <NavButton 
                        href={item.href}
                        active={item.active ? 1 : 0}
                        trigger={false}
                      >
                        {item.label}
                      </NavButton>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            position: { xs: 'absolute', md: 'relative' },
            right: { xs: '16px', md: '0' },
            padding: '0 16px'
          }}>
            <Box className="expand-btn-inner">
              <HamburgerMenu onClick={toggleDrawer(true)}>
                <DotRow>
                  <Dot />
                  <Dot />
                  <Dot />
                </DotRow>
                <DotRow>
                  <Dot />
                  <Dot />
                  <Dot />
                </DotRow>
                <DotRow>
                  <Dot />
                  <Dot />
                  <Dot />
                </DotRow>
              </HamburgerMenu>
            </Box>
          </Box>
        </Container>
      </MainMenuBar>
      
      {/* Sticky Header - Only visible on scroll */}
      <Box className="menu-area">
        <StyledAppBar trigger={trigger}>
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              py: 1
            }}>
              <LogoContainer>
                <Box component="a" href="#rs-header" sx={{ height: '100px', verticalAlign: 'middle', display: 'flex', alignItems: 'center' }}>
                  <Box 
                    component="img" 
                    src="/assets/images/logo-dark.png" 
                    alt="HK Managed Security Provider" 
                    sx={{ height: '60px' }}
                  />
                </Box>
              </LogoContainer>
              
              {!isMobile && (
                <NavContainer>
                  <Box component="nav" sx={{ display: 'flex' }}>
                    <Box component="ul" sx={{ 
                      display: 'flex', 
                      listStyle: 'none', 
                      m: 0, 
                      p: 0 
                    }}>
                      {menuItems.map((item) => (
                        <Box component="li" key={item.label} sx={{ mx: 0 }}>
                          <NavButton 
                            href={item.href}
                            active={item.active ? 1 : 0}
                            trigger={true}
                          >
                            {item.label}
                          </NavButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </NavContainer>
              )}

            <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center' }}>
              <Box className="expand-btn-inner">
                <HamburgerMenu onClick={toggleDrawer(true)}>
                  <DotRow>
                    <Dot />
                    <Dot />
                    <Dot />
                  </DotRow>
                  <DotRow>
                    <Dot />
                    <Dot />
                    <Dot />
                  </DotRow>
                  <DotRow>
                    <Dot />
                    <Dot />
                    <Dot />
                  </DotRow>
                </HamburgerMenu>
              </Box>
            </Box>
            </Box>
          </Container>
        </StyledAppBar>
      </Box>

      {/* Mobile Drawer - Canvas Menu */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 500 },
            backgroundColor: '#101010',
            padding: '20px',
          }
        }}
      >
        <DrawerContainer role="presentation">
          <CloseButton onClick={toggleDrawer(false)}>
            <CloseLine />
            <CloseLine />
          </CloseButton>
          
          <CanvasLogo>
            <Box 
              component="img" 
              src="/assets/images/logo.png" 
              alt="HK Managed Services Provider" 
              sx={{ height: '50px' }}
            />
          </CanvasLogo>
          
          <CanvasText variant="body1">
            Cybersecurity to the next level
          </CanvasText>
          
          <MediaImage>
            <img src="/assets/images/banner/style1/main-banner.png" alt="HKMSP Banner" />
          </MediaImage>
          
          <List sx={{ mb: 4 }}>
            {menuItems.map((item) => (
              <CanvasMenuItem 
                key={item.label} 
                component="a" 
                href={item.href}
                onClick={toggleDrawer(false)}
              >
                <ListItemText primary={item.label} />
              </CanvasMenuItem>
            ))}
          </List>
          
          <CanvasContact>
            <ContactTitle variant="h6">
              Contact Info
            </ContactTitle>
            
            <ContactList>
              <Box sx={{ color: '#bb0b0b', mt: 1 }}>
                <LocationOnIcon />
              </Box>
              <Box>
                <ContactInfo variant="h6" sx={{ mb: 1, fontSize: '18px' }}>
                  Address
                </ContactInfo>
                <ContactInfo variant="body2">
                  Unit 2A, 17/F, Glenealy Tower, No.1 Glenealy<br />Central, Hong Kong
                </ContactInfo>
              </Box>
            </ContactList>
            
            <ContactList>
              <Box sx={{ color: '#bb0b0b', mt: 1 }}>
                <EmailIcon />
              </Box>
              <Box>
                <ContactInfo variant="h6" sx={{ mb: 1, fontSize: '18px' }}>
                  Email
                </ContactInfo>
                <ContactInfo 
                  variant="body2" 
                  component="a" 
                  href="mailto:info@hkmsp.com" 
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                  info@hkmsp.com
                </ContactInfo>
              </Box>
            </ContactList>
            
            <ContactList>
              <Box sx={{ color: '#bb0b0b', mt: 1 }}>
                <PhoneIcon />
              </Box>
              <Box>
                <ContactInfo variant="h6" sx={{ mb: 1, fontSize: '18px' }}>
                  Phone
                </ContactInfo>
                <ContactInfo 
                  variant="body2" 
                  component="a" 
                  href="tel:(+1)213-349-8776" 
                  sx={{ color: 'inherit', textDecoration: 'none' }}
                >
                  (+1) 213-349-8776
                </ContactInfo>
              </Box>
            </ContactList>
          </CanvasContact>
        </DrawerContainer>
      </Drawer>
    </Box>
  );
}

export default HeaderMain;
